import * as React from 'react'

import { Box, Button, Section, Text } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import {
  EmployeeBlocksEmployeeFragment,
  EmployeeBlocksEmployeeFragmentDoc,
  EmployeeBlocksUpdateEmployeeIdentificationDataDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import SelectField from 'modules/core/components/SelectField'
import TextField from 'modules/core/components/TextField'
import createAsyncData from 'modules/core/hooks/createAsyncData'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { stringToDate } from 'modules/core/lib/dates'
import { formGetDirtyFields } from 'modules/core/lib/formGetDirtyFields'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useAddListToContractCuesContext,
  useHasContractCue,
} from 'modules/contracts/hooks/useHasContractCue'
import ContractCuesAlert from 'modules/contracts/pages/ContractVersionsPage/ContractCuesAlert'
import { IdentifierTypes } from 'modules/employees/store/collections/identifierTypes'
import { Country } from 'modules/legal_entities/lib/constants'
import idValidationStrategies from 'modules/legal_entities/lib/identifierValidators'
import getMaxLengthByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/fieldLength'
import getPlaceholderByCountryAndIdType from 'modules/legal_entities/lib/identifierValidators/placeholders'
import { SpainIdType } from 'modules/legal_entities/lib/identifierValidators/spain/idType'
import Identifiers from 'modules/payroll_employees/components/Identifiers'

import WorkPermit from './WorkPermit'

interface Props {
  employee: EmployeeBlocksEmployeeFragment
  readonly: boolean
  payroll: boolean
  isCurrent: boolean
}

const useFetch = createAsyncData((identifierTypes, country) =>
  identifierTypes.fetch({
    data: {
      country,
    },
  })
)

class IdentificationForm extends Form {
  constructor(values: Record<string, unknown>, includeGenericSsn: boolean) {
    const schema: Record<string, string> = {
      identifier: 'string',
      identifier_type: 'string',
      identifier_expiration_date: 'date',
      has_work_permit: 'boolean',
    }
    if (includeGenericSsn) {
      schema.social_security_number = 'string'
    }
    super(values, schema)
  }
}

export const Identification = observer((props: Props) => {
  const { employee, payroll, readonly } = props
  const { chooseIdentityDocument, addIdNumber, addSocialSecurityNumber } =
    trackingEvents.employee.personal

  const rawCountry = employee.legalEntity?.country
  const countryEnumValue =
    rawCountry === Country.Spain ||
    rawCountry === Country.France ||
    rawCountry === Country.Germany ||
    rawCountry === Country.Italy ||
    rawCountry === Country.Portugal
      ? rawCountry
      : undefined

  // Only Spain uses the generic SSN field stored in `social_security_number`.
  // Countries with payroll support (DE, IT, PT) have dedicated `Identifiers` components
  // that render their own SSN fields. All other countries have no SSN field.
  const includeGenericSsn = rawCountry === Country.Spain

  const identifierTypes = React.useRef(new IdentifierTypes()).current
  const loading = useFetch(identifierTypes, employee.legalEntity?.country)

  const options = identifierTypes.models.map(
    (item): SelectOption => ({
      value: item.attributes.slug,
      label: item.attributes.label,
    })
  )

  const [updateIdentificationData] = useMutation(
    EmployeeBlocksUpdateEmployeeIdentificationDataDocument,
    {
      extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
      update: (cache, result) => {
        const updatedEmployee = result.data?.employeesMutations?.updateEmployeesEmployee?.employee
        if (updatedEmployee) {
          cache.writeFragment({
            id: cache.identify(employee),
            fragment: EmployeeBlocksEmployeeFragmentDoc,
            fragmentName: 'EmployeeBlocksEmployee',
            data: {
              ...employee,
              hasWorkPermit: updatedEmployee.hasWorkPermit,
              identifier: updatedEmployee.identifier,
              identifierExpirationDate: updatedEmployee.identifierExpirationDate,
              identifierType: updatedEmployee.identifierType,
              socialSecurityNumber: updatedEmployee.socialSecurityNumber,
            },
          })
        }
      },
    }
  )

  const form = React.useRef(
    new IdentificationForm(
      {
        identifier: employee.identifier,
        identifier_type: employee.identifierType,
        identifier_expiration_date: employee.identifierExpirationDate,
        has_work_permit: employee.hasWorkPermit,
        social_security_number: employee.socialSecurityNumber,
      },
      includeGenericSsn
    )
  )
  const identificationForm = form.current

  const [[submitEventHandler], setSubmitEventHandler] = React.useState<[() => void] | [null]>([
    null,
  ])

  const [countrySpecificIdentifiersDirty, setIdentifiersDirty] = React.useState(false)
  const maxDate = moment().clone().add(14, 'years')

  const trackEvent = () => {
    track(trackingEvents.employee.editEmployeeInformationSubmit, {
      fields_edited: formGetDirtyFields(identificationForm),
      legal_entity_country: employee.legalEntity?.country,
      layout_version: 'v1',
    })
  }

  const onSubmit = async () => {
    submitEventHandler?.()

    trackEvent()

    const identifierExpirationDateValue = identificationForm.get('identifier_expiration_date').value
    const identifierExpirationDate = identifierExpirationDateValue
      ? (() => {
          const date = stringToDate(identifierExpirationDateValue, 'ui')
          return new Date(Date.UTC(date.year(), date.month(), date.date())).toISOString()
        })()
      : undefined

    const result = await updateIdentificationData({
      variables: {
        employeeId: employee.id,
        identifier: identificationForm.get('identifier').value,
        identifierType: identificationForm.get('identifier_type').value,
        identifierExpirationDate: identifierExpirationDate,
        ...(identificationForm.get('identifier_type').value === 'nie'
          ? { hasWorkPermit: identificationForm.get('has_work_permit').value === true }
          : {}),
        ...(identificationForm.has('social_security_number')
          ? { socialSecurityNumber: identificationForm.get('social_security_number').value }
          : {}),
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        const errorKeys = Object.keys(errors)
        const message =
          errorKeys.length > 0
            ? errorKeys
                .map((key) => {
                  const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1)
                  return `${capitalizedKey}: ${errors[key]}`
                })
                .join(', ')
            : i18n.t('profile.employee.identification.update_identification_data_error')
        app.setFlash({
          type: 'wrong',
          message: message,
        })
      }
    })

    if (handled.success) {
      identificationForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.identification.update_identification_data'),
      })
    }

    return handled
  }

  const fieldLabels: Record<string, string> = {
    identifier_type: i18n.t('employee_form.new_identifier_type'),
    identifier: i18n.t('employee_form.identifier'),
    social_security_number: i18n.t('employee_form.social_security_number'),
    identifier_expiration_date: i18n.t('employee_form.identifier_expiration_date'),
  }

  const isNie = identificationForm.get('identifier_type').value === 'nie'
  const hasSocialSecurity = identificationForm.has('social_security_number')

  const hasContractCue = useHasContractCue()
  // field name in incidenceRules differs from name in this form
  let listOfCuedFields = []
  if (
    hasSocialSecurity &&
    hasContractCue('social_security_number') &&
    !identificationForm.get('social_security_number').originalValue
  ) {
    listOfCuedFields.push('social_security_number')
  }

  listOfCuedFields = listOfCuedFields.concat(
    Object.keys(identificationForm.fields).filter((field) => {
      return hasContractCue(field) && !identificationForm.get(field).originalValue
    })
  )

  const translatedList = listOfCuedFields.map((field) => fieldLabels[field])
  useAddListToContractCuesContext('identification', translatedList)

  // countryEnumValue computed above with safe narrowing

  const getOptionSlug = options.find(
    (x) => identificationForm.get('identifier_type').value === x.value
  )

  const identifier_type = options.some(
    (x) => x.value === identificationForm.get('identifier_type').value
  )
    ? identificationForm.get('identifier_type').value
    : ''

  const showError: string = identificationForm.get('identifier').isDirty
    ? countryEnumValue
      ? idValidationStrategies(
          countryEnumValue,
          identifier_type,
          identificationForm.get('identifier').value,
          getOptionSlug?.label || ''
        ).errors()
      : ''
    : ''

  const showSsnError =
    identificationForm.has('social_security_number') &&
    identificationForm.get('social_security_number').isDirty
      ? countryEnumValue === Country.Spain
        ? idValidationStrategies(
            Country.Spain,
            SpainIdType.SOCIAL_SECURITY_NUMBER,
            identificationForm.get('social_security_number').value,
            'SSN'
          ).errors()
        : ''
      : ''

  const disabled = !!showError || !!showSsnError

  const showIdentifierTypeSelect = !loading && options.length > 0

  const idFieldsDisabled: boolean =
    (showIdentifierTypeSelect && identificationForm.values.identifier_type === '') || readonly

  const showSubmit = identificationForm.isDirty || countrySpecificIdentifiersDirty

  return (
    <Box id="identification">
      <FormComponent onSubmit={onSubmit}>
        <FormRow multi>
          {showIdentifierTypeSelect && (
            <SelectField
              mandatory={hasContractCue('identifier_type')}
              label={fieldLabels.identifier_type}
              options={options}
              none={i18n.t('common.choose')}
              value={identificationForm.get('identifier_type').value}
              onChange={(option: string) => {
                identificationForm.setValues({ identifier_type: option })
              }}
              readonly={readonly}
              onBlur={() => track(chooseIdentityDocument)}
            />
          )}
        </FormRow>
        <FormRow>
          <TextField
            type={
              identificationForm.get('identifier_type').value === 'us_social_security'
                ? 'masked'
                : undefined
            }
            label={fieldLabels.identifier}
            value={identificationForm.get('identifier').value.toUpperCase()}
            onChange={(x) => identificationForm.setValues({ identifier: x })}
            error={showError}
            hideError
            mandatory
            onBlur={() => track(addIdNumber)}
            placeholder={
              countryEnumValue
                ? getPlaceholderByCountryAndIdType(countryEnumValue, identifier_type)
                : undefined
            }
            readonly={idFieldsDisabled}
            maxLength={
              countryEnumValue
                ? getMaxLengthByCountryAndIdType(countryEnumValue, identifier_type)
                : undefined
            }
          />
          {showError && (
            <Box alignItems="flexStart">
              <Text size="200" color="radicalRed1000" capitalize>
                {showError}
              </Text>
            </Box>
          )}
        </FormRow>
        <FormRow>
          <DayPicker
            mandatory={false}
            label={fieldLabels.identifier_expiration_date}
            field={identificationForm.get('identifier_expiration_date')}
            placeholder={i18n.t('common.select')}
            readonly={idFieldsDisabled}
            maxDate={maxDate}
            withNull
            withNullButtonLabel={i18n.t('common.clear')}
          />
        </FormRow>
        <FormRow>
          <Identifiers
            employeeId={employee.id}
            country={employee.legalEntity?.country ?? ''}
            mandatory={payroll}
            readonly={readonly}
            setSubmitEventHandler={(onSubmitEvent: (() => void) | null) =>
              setSubmitEventHandler([onSubmitEvent])
            }
            setIdentifiersDirty={setIdentifiersDirty}
          />
        </FormRow>
        {hasSocialSecurity && (
          <>
            <FormRow>
              <TextField
                type="masked"
                avoidPasswordManagers
                mandatory={payroll || hasContractCue('social_security_number')}
                label={fieldLabels.social_security_number}
                value={identificationForm.get('social_security_number').value}
                onChange={(x) => identificationForm.setValues({ social_security_number: x })}
                placeholder={
                  countryEnumValue === Country.Spain
                    ? getPlaceholderByCountryAndIdType(
                        Country.Spain,
                        SpainIdType.SOCIAL_SECURITY_NUMBER
                      )
                    : undefined
                }
                readonly={readonly}
                onBlur={() => track(addSocialSecurityNumber)}
                error={showSsnError}
                maxLength={
                  countryEnumValue === Country.Spain
                    ? getMaxLengthByCountryAndIdType(
                        Country.Spain,
                        SpainIdType.SOCIAL_SECURITY_NUMBER
                      )
                    : undefined
                }
              />
            </FormRow>
            {isNie && (
              <FormRow>
                <WorkPermit field={identificationForm.get('has_work_permit')} />
              </FormRow>
            )}
          </>
        )}
        <ContractCuesAlert fields={translatedList}></ContractCuesAlert>
        {showSubmit && (
          <FormRow footer>
            <Button disabled={disabled} submit>
              {i18n.t('common.submit')}
            </Button>
          </FormRow>
        )}
      </FormComponent>
    </Box>
  )
})

const IdentificationNumber = (props: Props) => {
  return (
    <Section
      illustration="id"
      title={i18n.t('employee_form.identification_number.new_title')}
      description={i18n.tCurrent(
        'employee_form.identification_number.new_description',
        props.isCurrent
      )}
    >
      <Box
        gap="s24"
        background="grey200"
        padding="s32"
        width="s360"
        alignSelf="flexEnd"
        borderRadius={{
          all: 'abs004',
        }}
      >
        <Identification {...props} />
      </Box>
    </Section>
  )
}

export default observer(IdentificationNumber)
